/**
 * Content Suite — wraps existing social/blog/ad tools + Social Accounts connect strip.
 */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Twitter, Pen, Mail, MessageSquare,
  Megaphone, LayoutGrid, BookOpen, FileText, Newspaper, ArrowRight, Lock,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, Pill, SectionHeading } from "@/components/premium/Primitives";
import SocialConnectStrip from "@/components/premium/SocialConnectStrip";

const SECTIONS = [
  {
    title: "Social media",
    tools: [
      { name: "Instagram Post", desc: "Caption + 30 hashtags + CTA", Icon: Instagram, to: "/tools/social-media", badge: "LIVE" },
      { name: "Facebook Post", desc: "Engagement-tuned copy", Icon: Facebook, to: "/tools/social-media", badge: "LIVE" },
      { name: "LinkedIn Post", desc: "Thought-leadership tone", Icon: Linkedin, to: "/tools/social-media", badge: "LIVE" },
      { name: "Twitter / X Thread", desc: "Hook + body + CTA tweet", Icon: Twitter, to: "/tools/social-media", badge: "LIVE" },
      { name: "WhatsApp Broadcast", desc: "Bulk message templates", Icon: MessageSquare, to: "/free/whatsapp-generator", badge: "LIVE" },
    ],
  },
  {
    title: "Long-form content",
    tools: [
      { name: "AI Blog Generator", desc: "Long-form SEO articles", Icon: BookOpen, to: "/ai-blog", badge: "LIVE" },
      { name: "Email Copy", desc: "Subject + body + CTA", Icon: Mail, to: "/tools/content", badge: "LIVE" },
      { name: "Newsletter", desc: "Weekly newsletter template", Icon: Newspaper, to: "/tools/content", badge: "LIVE" },
      { name: "Product Description", desc: "SEO product copy", Icon: Pen, to: "/tools/content", badge: "LIVE" },
      { name: "Press Release", desc: "Boilerplate + headline + quotes", Icon: FileText, to: "/content-suite/press-release", badge: "LIVE" },
      { name: "Case Study", desc: "Problem → solution → result", Icon: FileText, to: "/content-suite/case-study", badge: "LIVE" },
    ],
  },
  {
    title: "Ads & conversion",
    tools: [
      { name: "Google Ads Copy", desc: "Multi-variant ad headlines", Icon: Megaphone, to: "/tools/google-ads", badge: "LIVE" },
      { name: "Facebook Ads", desc: "Hook + body + CTA combos", Icon: Megaphone, to: "/tools/facebook-ads", badge: "LIVE" },
      { name: "Landing Page Copy", desc: "Hero + features + FAQ", Icon: LayoutGrid, to: "/tools/content", badge: "LIVE" },
    ],
  },
];

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, delay: d },
});

export default function PremiumContentSuite() {
  return (
    <div className="min-h-screen bg-white font-sans" data-testid="content-suite">
      <PremiumNav />

      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-6">
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] -z-0"
             style={{ background: "radial-gradient(circle, rgba(168,85,247,0.08) 0%, rgba(168,85,247,0) 60%)" }}/>
        <motion.div className="relative" {...fadeUp()}>
          <SectionHeading
            eyebrow="Content Suite"
            title="One input. Every channel. On brand."
            subtitle="Connect your social accounts once, then create and publish everywhere from a single prompt."
          />
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <SocialConnectStrip />

        {SECTIONS.map((sec, sIdx) => (
          <div key={sec.title} className={sIdx > 0 ? "mt-16" : ""}>
            <motion.h3 {...fadeUp(0.05)} className="font-heading text-xl font-semibold text-[#111111] mb-5">
              {sec.title}
            </motion.h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sec.tools.map((tool, idx) => {
                const isComing = tool.status === "coming-soon";
                const Wrapper = isComing ? "div" : Link;
                return (
                  <motion.div key={tool.name} {...fadeUp(0.04 * idx)}>
                    <Wrapper
                      {...(isComing ? {} : { to: tool.to || "#" })}
                      data-testid={`suite-tool-${tool.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <PremiumCard hover={!isComing}>
                        <div className="flex items-start gap-3">
                          <span className="w-10 h-10 rounded-xl bg-[#FAF5FF] flex items-center justify-center shrink-0">
                            <tool.Icon className="w-4 h-4 text-[#A855F7]" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-[#111111] truncate">{tool.name}</h4>
                              {tool.badge && !isComing && <Pill variant="success">{tool.badge}</Pill>}
                              {isComing && (
                                <Pill variant="neutral" icon={Lock}>SOON</Pill>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 leading-relaxed">{tool.desc}</p>
                          </div>
                          {!isComing && (
                            <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#A855F7] transition-colors" />
                          )}
                        </div>
                      </PremiumCard>
                    </Wrapper>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <PremiumFooter />
    </div>
  );
}
